import type { ReactNode } from 'react';
import { Fragment } from 'react';
import type { Theme } from '@mui/material';
import { CssBaseline, ThemeProvider as MUIThemeProvider, ScopedCssBaseline } from '@mui/material';

import useCurrentTheme from './useCurrentTheme';
import type { UseBuildThemeHookProps } from './useCurrentTheme';
import useCurrentLanguage from 'canvas_states/language/useCurrentLanguage';
import isRightToLeft from '../../utils/languages/isRightToLeft';

export interface ThemeProviderProps extends UseBuildThemeHookProps {
  children: ReactNode;
}

export const ThemeProviderMarkup: React.FC<{ theme: Theme; scopeCssBaseline?: boolean; children: ReactNode }> = ({
  theme,
  scopeCssBaseline = false,
  children,
}) => {
  const Wrapper = scopeCssBaseline ? ScopedCssBaseline : Fragment;

  return (
    <>
      {theme !== undefined && (
        <MUIThemeProvider theme={theme}>
          {!scopeCssBaseline && <CssBaseline />}
          <Wrapper>{children}</Wrapper>
        </MUIThemeProvider>
      )}
    </>
  );
};

/**
 * the default `ThemeProvider` that consumes `useAppConfig` and `useUrlInfo` hooks, among others
 */
const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const theme = useCurrentTheme();
  const currentLanguage = useCurrentLanguage().states.currentLanguage;

  return theme ? (
    <ThemeProviderMarkup theme={{ ...theme, direction: isRightToLeft(currentLanguage) ? 'rtl' : 'ltr' }}>
      {children}
    </ThemeProviderMarkup>
  ) : null;
};

ThemeProvider.displayName = 'ThemeProvider';

export default ThemeProvider;
